/*
 * Display text for leaf values. The one place for it.
 *
 * The same logic used to exist three times: datapoint page
 * (`LeafValueDisplay`), dashboard (`realDataWidgets`) and HMI. The copies
 * were "kept in sync by hand" and drifted anyway (An/Ein, dot/comma,
 * different decimals). A change here applies to all three.
 *
 * Type-only imports, so `tests/leafValue.test.ts` can load the file without
 * a bundler.
 */

import type { EnumEntryDto, FlagBitDto } from '../api/types';

/** Locale for value display. One place until the UI is translated (concept J.2). */
export const DISPLAY_LOCALE = 'de-AT';

/** Placeholder when there is no value. */
export const NO_VALUE = '—';

/**
 * What formatting needs to know about a leaf. Structurally compatible with
 * `LeafDto`, so callers pass the DTO directly.
 */
export interface LeafValueFormat {
  valueType?: string | null;
  decimals?: number | null;
  enumEntries?: readonly EnumEntryDto[] | null;
  flagBits?: readonly FlagBitDto[] | null;
}

const numberFormats = new Map<number, Intl.NumberFormat>();

/** Number with a fixed count of decimals in the display locale. */
export function formatNumber(value: number, decimals: number): string {
  const d = Math.max(0, Math.min(20, Math.trunc(decimals)));
  let format = numberFormats.get(d);
  if (!format) {
    format = new Intl.NumberFormat(DISPLAY_LOCALE, { minimumFractionDigits: d, maximumFractionDigits: d });
    numberFormats.set(d, format);
  }
  return format.format(value);
}

/** Decimals when neither caller nor leaf specify any. Fixed per magnitude,
 *  so the display does not jump between three and fifteen digits from one
 *  sample to the next. */
export function inferDecimals(value: number): number {
  const abs = Math.abs(value);
  if (abs >= 100) return 0;
  if (abs >= 10) return 1;
  return 2;
}

/** Boolean from a wire value. Some drivers send `1`, `"1"` or `"true"`
 *  instead of a real boolean; anything else is `null`. */
export function coerceBool(value: unknown): boolean | null {
  if (typeof value === 'boolean') return value;
  if (value === 1 || value === '1' || value === 'true' || value === 'True') return true;
  if (value === 0 || value === '0' || value === 'false' || value === 'False') return false;
  return null;
}

/** Value types that carry a unit. */
export function isNumericType(valueType: string | null | undefined): boolean {
  return valueType === 'Float' || valueType === 'Int' || valueType === 'Integer' || valueType === 'Number';
}

/**
 * Display text of an enum entry. Controllers often repeat the value in front
 * of the text ("3:Komfort", "15.Spreizung ..."); that prefix is dropped, but
 * only when the number equals the entry's value and no digit follows.
 * "2.5 bar" stays as it is.
 */
export function enumEntryLabel(entry: EnumEntryDto): string {
  const raw = entry.displayName && entry.displayName.length > 0 ? entry.displayName : entry.name;
  const match = /^\s*(-?\d+)\s*[:.]\s*(?=\D)(.+)$/.exec(raw);
  return match && Number(match[1]) === entry.value ? match[2].trim() : raw;
}

/** Display text of an enum value, or `null` when the leaf does not know it.
 *  Also accepts numbers that arrive as text. */
export function enumLabelFor(leaf: LeafValueFormat | null | undefined, value: unknown): string | null {
  if (!leaf?.enumEntries || leaf.enumEntries.length === 0) return null;
  const n = typeof value === 'number' ? value
    : typeof value === 'string' && value.trim() !== '' ? Number(value)
    : NaN;
  if (!Number.isFinite(n)) return null;
  const entry = leaf.enumEntries.find((e) => e.value === n);
  return entry ? enumEntryLabel(entry) : null;
}

/**
 * A value as display text.
 *
 * Order: enum text first (an operating mode "3" tells nobody anything), then
 * by value type, then by what actually arrives. `decimals` is the caller's
 * choice (e.g. a widget's) and wins over the leaf.
 */
export function formatLeafValue(
  value: unknown,
  leaf?: LeafValueFormat | null,
  decimals?: number | null,
): string {
  if (value === null || value === undefined) return NO_VALUE;

  const label = enumLabelFor(leaf, value);
  if (label !== null) return label;

  switch (leaf?.valueType) {
    case 'Bool':
    case 'Boolean': {
      const on = coerceBool(value);
      return on === null ? String(value) : boolLabel(on);
    }
    case 'Enum':
    case 'Enumeration':
      // Unknown code: show it raw, not as a measurement with decimals.
      return String(value);
    case 'Flags':
    case 'BitFlags':
      if (leaf.flagBits && typeof value === 'number') {
        const set = leaf.flagBits.filter((b) => (value & (1 << b.bit)) !== 0);
        return set.length === 0 ? '∅' : set.map((b) => b.displayName ?? b.name).join(', ');
      }
      return String(value);
    case 'Int':
    case 'Integer':
      if (typeof value === 'number' && Number.isFinite(value)) return formatNumber(Math.trunc(value), 0);
      break;
    default:
      break;
  }

  if (typeof value === 'boolean') return boolLabel(value);
  if (typeof value === 'number' && Number.isFinite(value)) {
    return formatNumber(value, decimals ?? leaf?.decimals ?? inferDecimals(value));
  }
  const text = String(value).trim();
  // Zeitpunkt vom Regler (BSB "dt": yyyy-MM-ddTHH:mm:ss) lesbar: 14.10.2026 12:58.
  const dt = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(text);
  if (dt) return `${dt[3]}.${dt[2]}.${dt[1]} ${dt[4]}:${dt[5]}`;
  return text.length > 0 ? text : NO_VALUE;
}

function boolLabel(on: boolean): string {
  return on ? 'An' : 'Aus';
}
